import React from "react";
import { BASE_TYPES } from "../constants";

const unitsMapper = {
  square_feet: "sq.ft.",
  running_feet: "rft.",
  number: "nos.",
};
const unitMapper = {
  square_feet: "/ sq.ft.",
  running_feet: "/ rft.",
  number: "/ no.",
};

const roundNumber = (number) => Math.round(number);
const formatQuantity = (number) => parseFloat(number.toFixed(2));

/**
 * Price breakdown for catalog product (internal access).
 * Mirrors ModulePriceDialog: base types from price_meta + Hardwares section from price_meta.hardwares.
 * @param {{ price_meta: object, totalPrice?: number, is_discounted?: boolean, original_price?: number }} props
 */
const ProductPriceBreakdown = ({
  price_meta,
  totalPrice,
  is_discounted,
  original_price,
}) => {
  if (!price_meta || typeof price_meta !== "object") return null;

  return (
    <div className="pricing-table-container" style={{ maxWidth: "100%", minWidth: 480 }}>
      <table className="pricing-table">
        <tbody>
          {BASE_TYPES.map(({ type, addonsType }, baseIndex) => {
            const meta = price_meta[type];
            if (!meta || (meta.measurement != null && meta.measurement <= 0)) return null;

            const addons = (meta.addons || [])
              .filter((addon) => addon && addon.price != null)
              .sort((x, y) => {
                const indexA = addonsType.indexOf(x.type);
                const indexB = addonsType.indexOf(y.type);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              });

            const isTypeShell = type === "shell";
            const baseTypeRate = meta.rate_with_addons ?? meta.base_rate;
            const baseTypePrice = meta.price_with_addons ?? meta.price;

            return (
              <tr key={`base_${baseIndex}`}>
                <td style={{ width: "300px" }}>
                  <div className="fw-bold">{meta.name || type}</div>
                  {addons.length > 0 && (
                    <div className="d-flex flex-wrap">
                      {addons.map((addon, addonIndex) => (
                        <span key={addonIndex}>
                          {addonIndex > 0 && <span className="mx-1">/</span>}
                          <span className="unit">{addon.name}</span>
                          <span className="ms-1">{addon.value_name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  ₹{roundNumber(isTypeShell ? baseTypePrice : baseTypeRate).toLocaleString("en-IN")}{" "}
                  <span className="unit">
                    {isTypeShell ? "/ no." : unitMapper[meta.unit] || ""}
                  </span>
                </td>
                <td>
                  {isTypeShell ? 1 : formatQuantity(meta.measurement ?? 0)}{" "}
                  <span className="unit">
                    {isTypeShell ? "no." : unitsMapper[meta.unit] || ""}
                  </span>
                </td>
                <td className="text-end">
                  ₹{roundNumber(baseTypePrice).toLocaleString("en-IN")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {price_meta.hardwares && Array.isArray(price_meta.hardwares.items) && price_meta.hardwares.items.length > 0 && (
        <>
          <div className="px-3 py-2 fw-bold bg-secondary-subtle border-top border-secondary-subtle">
            Hardwares
          </div>
          <table className="pricing-table">
            <tbody>
              {price_meta.hardwares.items.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ width: "240px" }}>
                    {item.name}
                  </td>
                  <td>
                    ₹{roundNumber(item.base_rate ?? 0).toLocaleString("en-IN")}{" "}
                    <span className="unit">{unitMapper[item.unit] || "/ no."}</span>
                  </td>
                  <td>
                    {formatQuantity(item.multiplier ?? 0)}{" "}
                    <span className="unit">{unitsMapper[item.unit] || "nos."}</span>
                  </td>
                  <td className="text-end">
                    ₹{roundNumber(item.price ?? 0).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 d-flex justify-content-end align-items-center border-top border-secondary-subtle">
            <span className="fw-bold me-2">Hardwares total</span>
            <span className="fw-bold">
              ₹{roundNumber(price_meta.hardwares.price_with_addons ?? price_meta.hardwares.price ?? 0).toLocaleString("en-IN")}
            </span>
          </div>
        </>
      )}

      <div className="p-3 border-top border-secondary-subtle">
        {is_discounted && original_price != null && (
          <div className="d-flex justify-content-end mb-2">
            <span className="fw-bold">Total</span>
            <span className="ms-2 grey_letter fw-bold text-decoration-line-through">
              ₹{roundNumber(original_price).toLocaleString("en-IN")}
            </span>
          </div>
        )}
        <div className="d-flex justify-content-end align-items-center">
          <span className="fw-bold">
            {is_discounted ? "After Discount" : "Total"}
          </span>
          <span className="fs-14px ms-2 active-dimension fw-bold">
            ₹{totalPrice != null ? roundNumber(totalPrice).toLocaleString("en-IN") : "—"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductPriceBreakdown;
